import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, DownloadSimple, ArrowCounterClockwise, Coins, Timer, WarningOctagon, Users, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fetchBlobUrl, artifactUrl, fileDownloadUrl } from "../lib/api";
import StageTracker from "../components/StageTracker";
import { useAuth } from "../context/AuthContext";
import ABHarness from "./ABHarness";

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-[#18181B] p-4">
      <div className="flex items-center gap-2 overline mb-2"><Icon size={13} /> {label}</div>
      <div className="font-mono font-bold text-xl" style={{ color }}>{value}</div>
    </div>
  );
}

export default function RunDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [run, setRun] = useState(null);
  const [outUrl, setOutUrl] = useState(null);
  const [simStage, setSimStage] = useState("tts");
  const [confirmDel, setConfirmDel] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/runs/${id}`);
    setRun(data);
    return data;
  }, [id]);

  useEffect(() => { load().catch(() => toast.error("Run not found")); }, [load]);

  useEffect(() => {
    if (!run || run.status !== "running") return;
    const t = setInterval(() => { load(); }, 1500);
    return () => clearInterval(t);
  }, [run, load]);

  useEffect(() => {
    if (run?.output_path && !outUrl) fetchBlobUrl(run.output_path).then(setOutUrl).catch(() => {});
  }, [run, outUrl]);

  const retry = async () => { const { data } = await api.post(`/runs/${id}/retry`); toast.info(`Resuming from “${data.resumed_from}” …`); load(); };
  const del = async () => {
    try { await api.delete(`/runs/${id}`); toast.success("Run deleted."); nav("/history"); }
    catch (e) { toast.error(e?.response?.status === 403 ? "Not allowed to delete this run." : "Delete failed."); }
  };
  const simulateFail = async () => {
    await api.post(`/runs/${id}/simulate_failure`, { stage: simStage });
    toast.warning(`Simulated failure at “${simStage}”. Hit resume to re-run from there.`);
    load();
  };

  if (!run) return <div className="font-mono text-muted-foreground">loading run…</div>;

  const done = run.status === "completed";
  const retryable = run.status === "failed" || run.status === "interrupted";
  const canDelete = user && (user.role === "admin" || user.email === run.user_email);
  const dirLabel = run.direction === "hi-en" ? "Hindi → English" : "English → Hindi";

  return (
    <div>
      <button onClick={() => nav(-1)} className="flex items-center gap-2 font-mono text-xs text-muted-foreground hover:text-foreground transition-colors mb-6"><ArrowLeft size={14} /> back</button>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">{dirLabel} · run {run.id.slice(0, 8)}</div>
          <h1 className="font-mono font-extrabold tracking-tighter text-2xl">{run.original_filename}</h1>
        </div>
        <div className="flex items-center gap-3">
          <span data-testid="run-status" className="font-mono text-sm px-3 py-1.5 border border-border" style={{ color: done ? "#00E559" : retryable ? "#FF3333" : "#FF5500" }}>{run.status}{run.status === "failed" && run.failed_stage ? ` · ${run.failed_stage}` : ""}</span>
          {retryable && (
            <button data-testid="retry-button" onClick={retry} className="flex items-center gap-2 font-mono text-sm bg-primary text-black px-3 py-1.5 hover:bg-[#FF7733] transition-colors"><ArrowCounterClockwise size={15} weight="bold" /> resume{run.failed_stage ? ` from ${run.failed_stage}` : ""}</button>
          )}
          {canDelete && (confirmDel ? (
            <span className="flex items-center gap-2">
              <button data-testid="confirm-delete-run" onClick={del} className="font-mono text-sm bg-[#FF3333] text-white px-3 py-1.5 hover:bg-[#ff5555] transition-colors">confirm delete</button>
              <button data-testid="cancel-delete-run" onClick={() => setConfirmDel(false)} className="font-mono text-sm border border-border px-3 py-1.5 hover:bg-secondary transition-colors">cancel</button>
            </span>
          ) : (
            <button data-testid="delete-run-button" onClick={() => setConfirmDel(true)} className="flex items-center gap-2 font-mono text-sm border border-[#FF3333] text-[#FF3333] px-3 py-1.5 hover:bg-[#FF3333] hover:text-white transition-colors"><Trash size={15} /> delete</button>
          ))}
        </div>
      </div>

      {done && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-border border border-border mb-8">
          <Stat icon={Timer} label="duration" value={`${run.duration_s}s`} />
          <Stat icon={Users} label="speakers" value={run.speakers} />
          <Stat icon={Coins} label="cost / min" value={`$${run.cost_per_min}`} color={run.cost_per_min < 0.15 ? "#00E559" : "#FF3333"} />
          <Stat icon={Coins} label="total cost" value={`$${run.total_cost}`} />
          <Stat icon={WarningOctagon} label="wasted gpu" value={`${run.wasted_compute_pct}%`} color={run.wasted_compute_pct < 15 ? "#00E559" : "#FF3333"} />
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <div className="overline mb-4">pipeline · 6 stages</div>
          <StageTracker stages={run.stages} />
          {(done || run.status === "failed") && (
            <div className="mt-4 border border-border bg-[#18181B] p-4">
              <div className="overline mb-3">resume test · simulate a stage failure</div>
              <div className="flex flex-wrap items-center gap-2">
                <select data-testid="sim-stage-select" value={simStage} onChange={(e) => setSimStage(e.target.value)}
                  className="bg-background border border-border px-3 py-2 font-mono text-xs focus:border-primary outline-none">
                  {run.stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <button data-testid="sim-fail-button" onClick={simulateFail}
                  className="font-mono text-xs px-3 py-2 border border-destructive text-destructive hover:bg-destructive hover:text-black transition-colors">
                  fail this stage
                </button>
              </div>
              <div className="text-xs text-muted-foreground mt-2 leading-relaxed">
                Marks the chosen stage as failed while keeping earlier stages intact. Then hit <span className="text-primary">resume</span> — only the failed stage onward re-runs.
              </div>
            </div>
          )}
        </div>

        <div className="space-y-8">
          {done && (
            <div>
              <div className="overline mb-4">dubbed output <span className="text-success">[real dub]</span></div>
              {outUrl ? <AudioPlayerLazy src={outUrl} /> : <div className="bg-[#18181B] border border-border p-6 font-mono text-sm text-muted-foreground">loading audio…</div>}
              <div className="mt-3 text-xs text-muted-foreground font-mono leading-relaxed">
                Fully orchestrated: WhisperX → pyannote → Gemini 3 Flash → XTTS-v2 → Demucs → FFmpeg. Voice-cloned TTS fitted to source timing and mixed over the preserved background stem.
              </div>
            </div>
          )}

          {(run.transcript || run.translation) && (
            <div>
              <div className="overline mb-4">intermediate artifacts</div>
              <div className="border border-border">
                <ArtifactRow label="transcript.json" desc={`${run.transcript?.length || 0} segments · word timestamps`} href={artifactUrl(run.id, "transcript")} disabled={!run.transcript} testid="download-transcript" />
                <ArtifactRow label="translation.json" desc="Gemini 3 Flash · spoken register" href={artifactUrl(run.id, "translation")} disabled={!run.translation} testid="download-translation" />
                <ArtifactRow label="source-audio" desc={run.original_filename} href={fileDownloadUrl(run.input_path)} testid="download-source" />
                {run.output_path && <ArtifactRow label="dubbed-output" desc="final remix (mocked)" href={fileDownloadUrl(run.output_path)} testid="download-output" />}
              </div>
            </div>
          )}

          {run.transcript && run.translation && (
            <div>
              <div className="overline mb-4">segment alignment</div>
              <div className="border border-border max-h-72 overflow-auto">
                {run.transcript.map((seg, i) => (
                  <div key={i} className="p-3 border-b border-border last:border-b-0 bg-[#18181B]" data-testid={`segment-${i}`}>
                    <div className="flex items-center gap-3 mb-1.5">
                      <span className="font-mono text-xs text-primary">{seg.start.toFixed(1)}–{seg.end.toFixed(1)}s</span>
                      <span className="overline" style={{ fontSize: "0.5rem" }}>{seg.speaker}</span>
                    </div>
                    <div className="text-sm mb-1">{seg.text}</div>
                    <div className="text-sm text-muted-foreground">→ {run.translation[i]?.text}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {done && <ABHarness run={run} reload={load} />}
    </div>
  );
}

function ArtifactRow({ label, desc, href, disabled, testid }) {
  return (
    <a data-testid={testid} href={disabled ? undefined : href} target="_blank" rel="noreferrer"
      className={`flex items-center justify-between p-4 border-b border-border last:border-b-0 bg-[#18181B] transition-colors ${disabled ? "opacity-40 pointer-events-none" : "hover:bg-secondary"}`}>
      <div><div className="font-mono text-sm">{label}</div><div className="text-xs text-muted-foreground">{desc}</div></div>
      <DownloadSimple size={18} className="text-muted-foreground" />
    </a>
  );
}

// lazy import to reuse player component
import AudioPlayer from "../components/AudioPlayer";
function AudioPlayerLazy({ src }) { return <AudioPlayer src={src} testid="output-audio-player" />; }
